import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Navigate } from "react-router-dom";

import { useUsers } from "~/hooks";
import { MODAL_ROUTES, ROUTES, useNavigateModal } from "~/router";
import { usePermissions } from "~/screens/Admins/hooks/usePermissions";
import { type ModalProps } from "~/shared.types";
import { useUserStore } from "~/stores";
import { icons, Modal } from "~/ui";
import { errorToast } from "~/ui/admin";
import {
  parseApiErrorMessage,
  parseExistingUser,
  type ExistingUser,
} from "~/utils";
import { addUserSchema, type AddUserFormData } from "./add-user-schema";
import { AddUserFields } from "./components/AddUserFields";
import { parseFieldErrors } from "./parse-create-user-error";

const ATHENA_NOTICE =
  "No Athena patient record is created here. At the first booking Elektra either matches an existing Athena patient or creates one, and the Patient ID is saved.";

export const AddUserModal = ({ show, onClose }: ModalProps) => {
  const navigateModal = useNavigateModal();
  const { createUserMutation } = useUsers();
  const { mutate: createUser, isPending } = createUserMutation;
  const role = useUserStore((state) => state.role);
  const { canCreateUser } = usePermissions();

  const [emailConflict, setEmailConflict] = useState<{
    email: string;
    user: ExistingUser;
  } | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<AddUserFormData>({
    resolver: zodResolver(addUserSchema),
    defaultValues: { firstName: "", lastName: "", email: "" },
  });

  const values = watch();

  const existingUser =
    emailConflict && emailConflict.email === values.email.trim()
      ? emailConflict.user
      : null;

  const hasEveryField = Boolean(
    values.firstName && values.lastName && values.email,
  );

  const openUserDetails = (userId: string) => {
    navigateModal(
      `${MODAL_ROUTES.userDetails}/${userId}`,
      {},
      { replace: true },
    );
  };

  const onSubmit = (data: AddUserFormData) => {
    setEmailConflict(null);

    createUser(data, {
      onSuccess: (response) => openUserDetails(response.data.id),
      onError: (error) => {
        const conflictingUser = parseExistingUser(error);

        if (conflictingUser) {
          setEmailConflict({ email: data.email, user: conflictingUser });
          return;
        }

        const fieldErrors = parseFieldErrors(error);
        const fields = Object.keys(fieldErrors) as (keyof AddUserFormData)[];

        if (fields.length) {
          fields.forEach((field) => {
            setError(field, { message: fieldErrors[field] });
          });
          return;
        }

        errorToast(parseApiErrorMessage(error));
      },
    });
  };

  if (!role) return null;

  if (!canCreateUser) return <Navigate to={ROUTES.dashboard} replace />;

  return (
    <Modal
      className="gap-6"
      show={show}
      title="Add User"
      description="Creates a new Elektra account."
      onClose={onClose}
      confirmLabel="Create user"
      handleConfirm={handleSubmit(onSubmit)}
      disableConfirm={!hasEveryField}
      isLoading={isPending}
    >
      <form
        className="flex h-full flex-col gap-6"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <AddUserFields
          register={register}
          errors={errors}
          existingUser={existingUser}
          isSubmitting={isPending}
          onOpenExistingUser={openUserDetails}
        />

        <div className="border-light-lavender-gray mt-auto flex gap-2 rounded-md border bg-neutral-100 p-4 text-sm text-neutral-600">
          <icons.outline.InformationCircleIcon className="mt-0.5 size-4 shrink-0" />
          <p>{ATHENA_NOTICE}</p>
        </div>

        <button
          type="submit"
          className="hidden"
          tabIndex={-1}
          aria-hidden="true"
          disabled={!hasEveryField || isPending}
        />
      </form>
    </Modal>
  );
};
